/**
 * Admin table view for listing and filtering spaces (question instances):
 * `<SpacesTable>` and `useSpaceList`.
 *
 * `<SpacesTable>` is config-driven via `SpacesTableConfig`: features push
 * `columns`, `filters`, and an optional `searchFilter` into the config, and it
 * renders them through `PaginatedTable` with `Space` rows.
 */
import type { Space } from '@repo/shared/orpc/procedures/spaces';
import { PaginatedTable, type TableFilter, type TableListWidgetProps, type TableSearchFunc } from 'component/dashboard/paginated-table';
import type { TableColumn } from 'component/dashboard/table-grid';
import { useAllSpaces } from 'data/spaces';
import type React from 'react';
import { useConfig } from 'util/features-hooks';
import type { TLabel } from 'util/stdtypes';

export type SpaceFilter = TableFilter<Space>;
export type SpaceSearchFunc = TableSearchFunc<Space>;
export type SpacesTableListWidgetProps = TableListWidgetProps<Space>;

function spaceKeyOf(space: Space): string {
    return space.spaceKey;
}

export function SpacesTable({ itemList }: { itemList: Space[] | undefined }) {
    const { filters, spaceTableRightWidgets, emptyTableWidget, searchFilter, tableTitleLabel, columns } = useConfig<SpacesTableConfig>();
    return (
        <PaginatedTable
            columns={columns}
            emptyWidget={emptyTableWidget}
            filters={filters}
            itemKey={spaceKeyOf}
            itemList={itemList}
            rightWidgets={spaceTableRightWidgets}
            searchFilter={searchFilter}
            titleLabel={tableTitleLabel}
        />
    );
}

export type SpacesTableConfig = {
    spaceTableRightWidgets: React.FunctionComponent[];
    filters: SpaceFilter[];
    searchFilter?: SpaceSearchFunc;
    columns: TableColumn<Space>[];
    emptyTableWidget?: React.ComponentType<SpacesTableListWidgetProps>;
    tableTitleLabel: TLabel;
};

export function useSpaceList(): Space[] | undefined {
    const { spaces, isLoading } = useAllSpaces();
    if (isLoading) return undefined;
    return spaces;
}
